import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type FraudWarning = {
  type: string;
  ip?: string | null;
  created: string;
  updated: string;
  relatedStatusIds: string[];
  relatedStatuses?: Array<{ statusId: string; userId: string }>;
  fraudProbability?: number | null;
  matchedTexts?: Array<{ ownAnswer: string; otherAnswer: string; similarity: number }>;
  copyStats?: {
    copiedTeilaufgabenCount: number;
    totalTeilaufgabenCount: number;
    threshold: number;
    ratio?: number;
    copiedTeilaufgaben: Array<{
      teilaufgabenId: string;
      teilaufgabenTitle: string;
      teilaufgabenIndex: number;
      aufgabenIndex: number;
    }>;
  } | null;
  llmDetectionDetails?: Array<{
    teilaufgabenId: string;
    answer: string;
    llmProbability: number;
    reasoning: string;
    teilaufgabenTitle?: string;
    teilaufgabenIndex?: number;
    aufgabenIndex?: number;
  }>;
};

export type FraudWarningItem = {
  statusId: string;
  userId: string;
  fallaufgabenId: string;
  fallaufgabenName: string;
  versionsUnique: string;
  created: string;
  startDate: string;
  endDate: string;
  acknowledged: boolean;
  fraudWarnings: FraudWarning[];
};

export type FraudWarningDetails = {
  statusId: string;
  userId: string;
  fallaufgabenId: string;
  fallaufgabenName: string;
  versionsUnique: string;
  created: string;
  startDate: string;
  endDate: string;
  ipHistory: Array<{ ip: string; created: string; updated: string }>;
  fraudWarnings: FraudWarning[];
};

@Injectable({
  providedIn: 'root'
})
export class FraudWarningsService {
  public items: FraudWarningItem[] = [];
  private baseRoute = "/api/admin/fraudwarnings";
  private http = inject(HttpClient);

  public async list() {
    const rawItems = await firstValueFrom(
      this.http.get<FraudWarningItem[]>(`${this.baseRoute}/list`)
    );

    // Be tolerant to older backend responses that might not include relatedStatuses yet.
    this.items = (rawItems ?? []).map((item) => ({
      ...item,
      fraudWarnings: (item.fraudWarnings ?? []).map((warning) => ({
        ...warning,
        relatedStatuses: warning.relatedStatuses ?? [],
        copyStats: warning.copyStats ?? null,
        llmDetectionDetails: warning.llmDetectionDetails ?? [],
      })),
    }));

    return this.items;
  }

  public async getDetails(statusId: string) {
    return await firstValueFrom(
      this.http.post<FraudWarningDetails>(`${this.baseRoute}/get-details/${statusId}`, {})
    );
  }

  public async acknowledge(statusId: string) {
    await firstValueFrom(
      this.http.post(`${this.baseRoute}/acknowledge`, { statusId })
    );
    const item = this.items.find((i) => i.statusId === statusId);
    if (item) item.acknowledged = true;
  }

  public async unacknowledge(statusId: string) {
    await firstValueFrom(
      this.http.post(`${this.baseRoute}/unacknowledge`, { statusId })
    );
    const item = this.items.find((i) => i.statusId === statusId);
    if (item) item.acknowledged = false;
  }
}
